/**
 * car.ts — Categoría "Carnes" (10 productos demo).
 * Insumos principales para parrillas, restaurantes y fast food.
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_car",
  skuPrefix: "CAR",
  categoria: "Carnes",
} as const;

export const cat_car_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Carne Molida de Res 1kg",
    marca: "Zenú",
    modelo: "Especial Molida",
    origen: "Colombia",
    costo: 19500,
    precio: 30_000,
    stock: 60,
    stockMinimo: 15,
    stockMaximo: 120,
    unidad: "kg",
    ubicacionFisica: "Congelador C1, carnes rojas",
    proveedor: "Distribuidora Nacional de Cárnicos",
    tiempoEntrega: "Diario",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    plu: 7,
    descripcionLarga:
      "Carne molida fresca de res, 85% magra. Empacada al vacío el día de proceso. Ideal para hamburguesas, bolognesa y albóndigas.\n\nRinde 4-5 hamburguesas por kg.",
    especificaciones: [
      { clave: "Corte", valor: "Molida 85/15" },
      { clave: "Empaque", valor: "Vacío 1kg" },
    ],
    imageKeywords: ["ground-beef", "carne-molida", "beef"],
    color: "bg-red-700",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Pechuga de Pollo 1kg",
    marca: "Pollos Bucanero",
    modelo: "Sin Hueso",
    origen: "Colombia",
    costo: 14500,
    precio: 22000,
    stock: 80,
    stockMinimo: 20,
    stockMaximo: 160,
    unidad: "kg",
    ubicacionFisica: "Congelador C2, aves",
    proveedor: "Pollos Bucanero S.A.",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    plu: 12,
    descripcionLarga:
      "Pechuga de pollo fresca deshuesada y sin piel. Cortes uniformes, baja en grasa. Ideal para grill, horno, sopas y ensaladas.",
    especificaciones: [
      { clave: "Corte", valor: "Filete sin hueso" },
      { clave: "Peso", valor: "1kg aprox" },
    ],
    imageKeywords: ["chicken-breast", "pechuga", "raw-chicken"],
    color: "bg-pink-200",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Chorizo Argentino x5",
    marca: "Zenú",
    modelo: "Argentino Parrillero",
    origen: "Colombia",
    costo: 12800,
    precio: 19500,
    stock: 90,
    stockMinimo: 20,
    stockMaximo: 170,
    unidad: "kg",
    ubicacionFisica: "Congelador C1, embutidos",
    proveedor: "Zenú Alimentos",
    vencimientoMeses: 2,
    pesoNeto: "500g",
    ventaPorPeso: true,
    plu: 13,
    descripcionLarga:
      "Chorizo argentino sazonado con especias naturales. Textura firme, sabor ahumado. Perfecto para parrillas y choripanes.",
    especificaciones: [
      { clave: "Unidades", valor: "5" },
      { clave: "Sabor", valor: "Argentino ahumado" },
    ],
    imageKeywords: ["chorizo", "argentine-sausage", "grill-meat"],
    color: "bg-red-800",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Salchicha Ranchera Zenú x8",
    marca: "Zenú",
    modelo: "Ranchera Ahumada",
    origen: "Colombia",
    costo: 8500,
    precio: 12900,
    stock: 130,
    stockMinimo: 28,
    stockMaximo: 240,
    unidad: "kg",
    ubicacionFisica: "Nevera C3, embutidos",
    proveedor: "Zenú Alimentos",
    vencimientoMeses: 3,
    pesoNeto: "400g",
    ventaPorPeso: true,
    plu: 15,
    descripcionLarga:
      "Salchicha tipo ranchera con textura firme y sabor ahumado. Ideal para perros calientes, parrilla y desayunos.",
    especificaciones: [
      { clave: "Unidades", valor: "8" },
      { clave: "Tipo", valor: "Ranchera ahumada" },
    ],
    imageKeywords: ["sausage", "salchicha", "hotdog-sausage"],
    color: "bg-red-500",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Tocineta Ahumada 250g",
    marca: "Zenú",
    modelo: "Ahumada Premium",
    origen: "Colombia",
    costo: 9800,
    precio: 14900,
    stock: 75,
    stockMinimo: 18,
    stockMaximo: 150,
    unidad: "kg",
    ubicacionFisica: "Nevera C3, embutidos",
    proveedor: "Zenú Alimentos",
    vencimientoMeses: 2,
    pesoNeto: "250g",
    ventaPorPeso: true,
    plu: 16,
    descripcionLarga:
      "Tocineta ahumada premium, lonchas delgadas y uniformes. Ideal para hamburguesas gourmet, ensaladas y desayunos americanos.",
    especificaciones: [
      { clave: "Formato", valor: "Lonchas delgadas" },
      { clave: "Ahumado", valor: "Natural" },
    ],
    imageKeywords: ["bacon", "tocineta", "smoked-meat"],
    color: "bg-red-600",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Bistec de Res 1kg",
    marca: "Cárnicos Premium",
    modelo: "Punta de Anca",
    origen: "Colombia",
    costo: 32500,
    precio: 49000,
    stock: 50,
    stockMinimo: 12,
    stockMaximo: 100,
    unidad: "kg",
    ubicacionFisica: "Congelador C1, carnes rojas",
    proveedor: "Distribuidora Nacional de Cárnicos",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    plu: 17,
    descripcionLarga:
      "Punta de anca madurada 14 días, marmoleo parejo. Corte premium para parrilla, plancha y stroganoff. Empacada al vacío.",
    especificaciones: [
      { clave: "Corte", valor: "Punta de anca" },
      { clave: "Maduración", valor: "14 días" },
    ],
    imageKeywords: ["beef-steak", "bistec", "raw-beef"],
    color: "bg-red-800",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Costilla de Cerdo 1kg",
    marca: "Cárnicos Premium",
    modelo: "Costilla Cargada",
    origen: "Colombia",
    costo: 18200,
    precio: 27500,
    stock: 55,
    stockMinimo: 12,
    stockMaximo: 100,
    unidad: "kg",
    ubicacionFisica: "Congelador C1, carnes rojas",
    proveedor: "Distribuidora Nacional de Cárnicos",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    plu: 18,
    descripcionLarga:
      "Costilla de cerdo cargada, ideal para asar al horno con BBQ o parrilla. Carne jugosa, cocción lenta.",
    especificaciones: [
      { clave: "Corte", valor: "Cargada con carne" },
      { clave: "Uso", valor: "Horno / parrilla" },
    ],
    imageKeywords: ["pork-ribs", "costilla-cerdo", "ribs"],
    color: "bg-red-700",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Solomillo de Res 1kg",
    marca: "Cárnicos Premium",
    modelo: "Solomillo Maduro",
    origen: "Colombia",
    costo: 48500,
    precio: 72900,
    stock: 30,
    stockMinimo: 8,
    stockMaximo: 60,
    unidad: "kg",
    ubicacionFisica: "Congelador C1, carnes rojas",
    proveedor: "Distribuidora Nacional de Cárnicos",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    plu: 19,
    descripcionLarga:
      "Solomillo de res, el corte más tierno. Madurado 21 días para máxima suavidad. Ideal para tournedos, medallones y roast beef.",
    especificaciones: [
      { clave: "Corte", valor: "Solomillo" },
      { clave: "Maduración", valor: "21 días" },
    ],
    imageKeywords: ["beef-tenderloin", "solomillo", "filet-mignon"],
    color: "bg-red-900",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Lomo de Cerdo 1kg",
    marca: "Cárnicos Premium",
    modelo: "Lomo Limpio",
    origen: "Colombia",
    costo: 16800,
    precio: 25500,
    stock: 60,
    stockMinimo: 15,
    stockMaximo: 120,
    unidad: "kg",
    ubicacionFisica: "Congelador C1, carnes rojas",
    proveedor: "Distribuidora Nacional de Cárnicos",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    plu: 20,
    descripcionLarga:
      "Lomo de cerdo limpio, sin hueso. Versátil para chuletas, estofado, apanado y asado. Magro y tierno.",
    especificaciones: [
      { clave: "Corte", valor: "Lomo sin hueso" },
      { clave: "Magro", valor: "Alto" },
    ],
    imageKeywords: ["pork-loin", "lomo", "pork"],
    color: "bg-pink-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Alitas de Pollo 1kg",
    marca: "Pollos Bucanero",
    modelo: "Alitas Frescas",
    origen: "Colombia",
    costo: 9500,
    precio: 14500,
    stock: 95,
    stockMinimo: 22,
    stockMaximo: 180,
    unidad: "kg",
    ubicacionFisica: "Congelador C2, aves",
    proveedor: "Pollos Bucanero S.A.",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    plu: 21,
    descripcionLarga:
      "Alitas de pollo enteras frescas, listas para marinar y freír o hornear. Ideales para bares, parrillas y eventos.",
    especificaciones: [
      { clave: "Unidades", valor: "~15 alitas" },
      { clave: "Corte", valor: "Entera sin separar" },
    ],
    imageKeywords: ["chicken-wings", "alitas", "raw-wings"],
    color: "bg-pink-300",
    shape: "square",
  }),
];
